import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  FilterField,
  FilterGrid,
  Form,
  InputGroup,
  ListPage,
  Modal,
  PageHeader,
  Row,
  Spinner,
} from '@/components/ui';
import { format } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FiltersCard from '../../../components/FiltersCard';
import ServerPagination from '../../../components/ServerPagination';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import { areasService } from '../../../services/areasService';
import { assetService } from '../../../services/assetService';
import { Asset } from '../../../types/Asset';
import {
  type CreatePcrSubmissionRequest,
  type PcrContractAreaOption,
  type PcrFilterAssetOption,
  type PcrForemanAreaOption,
  type PcrFilterOptionsResponse,
  type PcrListParams,
  type PcrMetricsResponse,
  type PcrReportTypeBucket,
  type PcrGroupedResult,
  PCR_DEFAULT_LIST_STATUSES,
  PCR_STATUS_OPTIONS,
  type PcrStatus,
  isDefaultPcrListStatusFilter,
  type PcrSubmission,
  createPcrSubmission,
  downloadPcrAuditReportPdf,
  downloadPcrMonthlyCsv,
  fetchPcrContractAreasForServiceArea,
  fetchPcrCreateContext,
  fetchPcrFilterOptions,
  fetchPcrGroupSuggestions,
  fetchPcrMetrics,
  fetchPcrSubmissionsPaged,
  formatPcrRoadAssetLabel,
  formatPcrServiceAreaLabel,
  formatPcrStatusLabel,
  generatePcrGroupSuggestions,
  isPcrReplyUiEnabled,
  normalizePcrReportTypeBucket,
  normalizePcrStatusParam,
  patchPcrSubmissionStatus,
  patchPcrSubmission,
} from '../../../services/pcrService';
import PcrMetricsSummaryCard from '../components/PcrMetricsSummaryCard';
import PcrNotesHistoryModal from '../components/PcrNotesHistoryModal';
import PcrReplyModal from '../components/PcrReplyModal';
import PcrSubmissionDetailModal from '../components/PcrSubmissionDetailModal';
import PcrSubmissionsTable from '../components/PcrSubmissionsTable';
import PcrPromoteToWorkIdModal from '../components/PcrPromoteToWorkIdModal';
import PcrCloseWithoutWorkModal from '../components/PcrCloseWithoutWorkModal';
import PcrGroupedTable from '../components/PcrGroupedTable';
import {
  canPrunePcrChildFilters,
  idsKey,
  prunePcrSelectedIds,
} from '../utils/pcrFilterPruning';

const PCR_SERVICE_AREA_IDS = ['16', '17', '22', '25'] as const;
const PCR_REPORT_TYPE_MULTI_OPTIONS: Array<{ id: PcrReportTypeBucket; name: string }> = [
  { id: 'Requests', name: 'Requests' },
  { id: 'Compliments', name: 'Compliments' },
  { id: 'Complaints', name: 'Complaints' },
  { id: 'N/A', name: 'N/A' },
];

function reportTypeBucketsKey(buckets: PcrReportTypeBucket[] | undefined): string {
  if (!buckets?.length) return '';
  return [...buckets].sort().join('\0');
}

function statusKey(statuses: PcrStatus[] | undefined): string {
  if (!statuses?.length) return '';
  return [...statuses].sort().join('\0');
}


/**
 * Reads a multi-value filter out of the URL.
 *
 * Accepts repeated keys (`serviceAreaId=16&serviceAreaId=17`), a single key (`serviceAreaId=16`),
 * and a comma-separated single key (`serviceAreaId=16,17`), across an ordered list of key aliases.
 * That is what keeps every bookmarked single-value URL working: `getAll` on a one-value param
 * simply returns a one-element list. An empty result means "no filter".
 */
function readMultiParam(sp: URLSearchParams, ...keys: string[]): string[] {
  const out: string[] = [];
  for (const key of keys) {
    for (const raw of sp.getAll(key)) {
      for (const token of raw.split(',')) {
        const trimmed = token.trim();
        if (trimmed && !out.includes(trimmed)) out.push(trimmed);
      }
    }
  }
  return out;
}

function parseServiceAreaIdsFromSearchParams(sp: URLSearchParams): string[] {
  return readMultiParam(sp, 'serviceAreaId').filter((t) =>
    (PCR_SERVICE_AREA_IDS as readonly string[]).includes(t),
  );
}

function parseRoadAssetIdsFromSearchParams(sp: URLSearchParams): string[] {
  // `assetId` is the legacy alias kept for older bookmarked links.
  return readMultiParam(sp, 'roadAssetId', 'assetId').filter(
    (t) => /^\d+$/.test(t) && parseInt(t, 10) >= 1,
  );
}

function parseForemanAreaIdsFromSearchParams(sp: URLSearchParams): string[] {
  return readMultiParam(sp, 'foremanAreaId').filter((t) => /^\d+$/.test(t));
}

function parseOneReportTypeBucket(v: string | null | undefined): PcrReportTypeBucket | undefined {
  const normalized = normalizePcrReportTypeBucket(v);
  const raw = (v || '').trim();
  return raw && raw.toLowerCase() === normalized.toLowerCase() ? normalized : undefined;
}

function parseReportTypeBucketsFromSearchParams(sp: URLSearchParams): PcrReportTypeBucket[] {
  const repeated = sp.getAll('reportTypeBucket');
  if (repeated.length > 0) {
    const out: PcrReportTypeBucket[] = [];
    const seen = new Set<PcrReportTypeBucket>();
    for (const r of repeated) {
      const b = parseOneReportTypeBucket(r);
      if (!b || seen.has(b)) continue;
      seen.add(b);
      out.push(b);
    }
    return out;
  }
  const legacy = parseOneReportTypeBucket(sp.get('reportTypeBucket') ?? sp.get('reportType'));
  return legacy ? [legacy] : [];
}

function statusesForListQuery(searchParams: URLSearchParams): PcrStatus[] {
  const fromUrl = searchParams
    .getAll('status')
    .map((s) => normalizePcrStatusParam(s))
    .filter((status): status is PcrStatus => status != null);
  return fromUrl.length > 0 ? fromUrl : [...PCR_DEFAULT_LIST_STATUSES];
}

/**
 * `PcrListParams` allows a single string on the multi-value id filters (older callers such as the
 * home PCR widgets still pass one). This dashboard always works in arrays, so narrow it once here
 * instead of re-checking `Array.isArray` at every use site.
 */
type PcrDashboardListParams = PcrListParams & {
  serviceAreaId: string[];
  foremanAreaId: string[];
  roadAssetId: string[];
  reportTypeBuckets: PcrReportTypeBucket[];
  statuses: PcrStatus[];
};

function readListParams(searchParams: URLSearchParams): PcrDashboardListParams {
  const statuses = statusesForListQuery(searchParams);
  const sortByRaw = searchParams.get('sortBy') || 'createdAt';
  const sortBy =
    sortByRaw === 'status' || sortByRaw === 'serviceArea' || sortByRaw === 'roadName'
      ? sortByRaw
      : 'createdAt';
  const sortDir = searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const pageSize = Math.min(200, Math.max(1, parseInt(searchParams.get('pageSize') || '20', 10) || 20));
  return {
    from: searchParams.get('from') || undefined,
    to: searchParams.get('to') || undefined,
    serviceArea: searchParams.get('serviceArea') || undefined,
    road: searchParams.get('road') || undefined,
    serviceAreaId: parseServiceAreaIdsFromSearchParams(searchParams),
    foremanAreaId: parseForemanAreaIdsFromSearchParams(searchParams),
    roadAssetId: parseRoadAssetIdsFromSearchParams(searchParams),
    reportTypeBuckets: parseReportTypeBucketsFromSearchParams(searchParams),
    statuses,
    page,
    pageSize,
    sortBy,
    sortDir,
    search: searchParams.get('search')?.trim() || undefined,
  };
}

/**
 * A dependent dropdown waiting for its parent: still a dropdown (border and chevron), greyed like
 * any disabled control. Inline because the filter grid's own input rule outranks utility classes.
 */
const DISABLED_DROPDOWN_STYLE: React.CSSProperties = {
  backgroundColor: '#f8fafc', // surface.alt
  color: '#64748b', // content.muted
};

/** sessionStorage page key for the remembered list state (V2-024, FLT-09). */
const PCR_PAGE_KEY = 'pcrs';

const PCR_REPORT_TYPE_OPTIONS = ['Requests', 'Complaints', 'Compliments'];

const PCR_ACTIVITY_TYPE_OPTIONS = [
  'Surface (patching, grading, sweeping, etc.)',
  'Drainage (culverts, ditching, etc.)',
  'Winter (plowing, sanding, etc.)',
  'Roadside (signs, mowing, rest areas, etc.)',
  'Traffic Maintenance (traffic control, vehicle incident, etc.)',
  'Structures (bridges, etc.)',
  'Network (traveling conditions, communications, etc.)',
  'Other',
];

function dedupeAssetsById(assets: Asset[]): Asset[] {
  const map = new Map<number, Asset>();
  for (const a of assets) {
    if (!map.has(a.id)) map.set(a.id, a);
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function filterRoadAssets(assets: Asset[]): Asset[] {
  const ROAD_TYPES = new Set(['road', 'highway']);
  return assets.filter((asset) => ROAD_TYPES.has((asset.assetType ?? '').toString().toLowerCase()));
}

function isPcrServiceAreaName(name: string): boolean {
  return /^service area\b/i.test(name.trim());
}

const PcrSubmissionsDashboardPage: React.FC = () => {
  const { loading: authLoading } = useAuth();
  const { hasPermission } = usePermissions();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const canPatchPcrSubmission =
    hasPermission('submission:pcr_update') || hasPermission('workId:create');
  const canSetPcrInternalReview = hasPermission('pcr:internal_review');
  const canFinalizePcr = hasPermission('pcr:finalize');
  /** Dashboard row “Edit” / PCR detail workflow: intake edit, Internal Review triage, or finalize. */
  const canEditPcrWorkflow =
    canPatchPcrSubmission || hasPermission('pcr:internal_review') || hasPermission('pcr:finalize');
  const canPcrReply = hasPermission('submission:pcr_reply');
  const pcrReplyUiEnabled = isPcrReplyUiEnabled();
  const canViewSubmissions = hasPermission('submission:view');
  const canCreatePcr = hasPermission('pcr:create');
  const canPromoteToWorkId = hasPermission('workId:create') && canFinalizePcr;
  const canOpenNotesHistory =
    hasPermission('submission:view') || hasPermission('pcr:history_view');
  const [searchParams, setSearchParams] = useSearchParams();
  const listParams = useMemo(() => readListParams(searchParams), [searchParams]);

  // V2-024 / FLT-09: the list state (filters, search, sort, page) lives in the URL; the last URL
  // is kept for the sign-in, so opening PCRs again without a query brings it back.
  const [savedQuery, setSavedQuery] = usePersistedListState(PCR_PAGE_KEY, 'query', '');
  const pendingRestoreRef = useRef<string | null>(
    searchParams.toString() === '' && savedQuery ? savedQuery : null,
  );

  useEffect(() => {
    if (pendingRestoreRef.current != null) {
      const restore = pendingRestoreRef.current;
      pendingRestoreRef.current = null;
      setSearchParams(new URLSearchParams(restore), { replace: true });
      return;
    }
    setSavedQuery(searchParams.toString());
    if (searchParams.getAll('status').length > 0) return;
    const next = new URLSearchParams(searchParams);
    for (const status of PCR_DEFAULT_LIST_STATUSES) {
      next.append('status', status);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setSavedQuery]);
  const [submissions, setSubmissions] = useState<PcrSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [metrics, setMetrics] = useState<PcrMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportMonth, setExportMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [exportCsvModalOpen, setExportCsvModalOpen] = useState(false);
  const [modalExportMonth, setModalExportMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [exporting, setExporting] = useState(false);
  const [replyModalRow, setReplyModalRow] = useState<PcrSubmission | null>(null);
  const [detailModalRow, setDetailModalRow] = useState<PcrSubmission | null>(null);
  const [detailStartInEditMode, setDetailStartInEditMode] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreatePcrSubmissionRequest>({
    report_id: '',
    service_area_text: '',
    community_text: '',
    road_text: '',
    report_type: '',
    activity_type: '',
    user_name: '',
    user_email: '',
    user_phone: '',
    contact_method: 'Email',
    message: '',
    image_url: '',
    is_feedback: false,
  });
  const [contactChannel, setContactChannel] = useState<'email' | 'phone'>('email');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [serviceAreas, setServiceAreas] = useState<{ id: number; name: string; code: string | null }[]>([]);
  const [contractAreas, setContractAreas] = useState<PcrContractAreaOption[]>([]);
  const [roadAssets, setRoadAssets] = useState<Asset[]>([]);
  const [loadingPcrFormRefs, setLoadingPcrFormRefs] = useState(false);
  const [selectedServiceAreaId, setSelectedServiceAreaId] = useState<number | null>(null);
  const [selectedContractAreaId, setSelectedContractAreaId] = useState<number | null>(null);
  /** When a “community” row is a foreman area with no linked contract areas (see GET …/contract-areas). */
  const [selectedForemanCommunityId, setSelectedForemanCommunityId] = useState<number | null>(null);
  const [reportIdPreviews, setReportIdPreviews] = useState<Record<string, string>>({});
  const [notesHistorySubmission, setNotesHistorySubmission] = useState<PcrSubmission | null>(null);
  const [notesHistoryInitialTab, setNotesHistoryInitialTab] = useState<'notes' | 'contact'>('notes');
  const [contactLogRefreshToken, setContactLogRefreshToken] = useState(0);
  const [auditReportExportingId, setAuditReportExportingId] = useState<number | null>(null);
  const [statusTransitioningId, setStatusTransitioningId] = useState<string | null>(null);
  const [promoteModalRow, setPromoteModalRow] = useState<PcrSubmission | null>(null);
  const [closeWithoutWorkRow, setCloseWithoutWorkRow] = useState<PcrSubmission | null>(null);
  // PCR Grouping / similarity view
  const [groupSimilar, setGroupSimilar] = useState(false);
  const [groupedResult, setGroupedResult] = useState<PcrGroupedResult | null>(null);
  const [groupedLoading, setGroupedLoading] = useState(false);
  const [groupedError, setGroupedError] = useState<string | null>(null);
  const [generatingGroups, setGeneratingGroups] = useState(false);
  const [searchInput, setSearchInput] = useState(listParams.search ?? '');

  useEffect(() => {
    setSearchInput(listParams.search?.trim() ?? '');
  }, [listParams.search]);

  // V2-077: the search field in the filter panel header runs this on Enter and on clear.
  const handleSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (!trimmed) next.delete('search');
    else next.set('search', trimmed);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const [draftFrom, setDraftFrom] = useState(listParams.from || '');
  const [draftTo, setDraftTo] = useState(listParams.to || '');
  const [draftServiceAreaIds, setDraftServiceAreaIds] = useState<string[]>(listParams.serviceAreaId);
  const [draftForemanAreaIds, setDraftForemanAreaIds] = useState<string[]>(listParams.foremanAreaId);
  const [draftRoadAssetIds, setDraftRoadAssetIds] = useState<string[]>(listParams.roadAssetId);
  const [draftReportTypeBuckets, setDraftReportTypeBuckets] = useState<PcrReportTypeBucket[]>(
    listParams.reportTypeBuckets ?? [],
  );
  const [draftStatuses, setDraftStatuses] = useState<PcrStatus[]>(
    listParams.statuses ?? [...PCR_DEFAULT_LIST_STATUSES],
  );
  const [filterOptions, setFilterOptions] = useState<PcrFilterOptionsResponse>({
    serviceAreas: [],
    foremanAreas: [],
    assets: [],
  });
  const [roadOptions, setRoadOptions] = useState<PcrFilterAssetOption[]>([]);
  const [foremanAreaOptions, setForemanAreaOptions] = useState<PcrForemanAreaOption[]>([]);
  /**
   * Which service area selection `roadOptions` / `foremanAreaOptions` currently describe
   * (an `idsKey`), or null when nothing is loaded for the current selection yet.
   *
   * This is the pruning gate. It must be a load-state flag rather than an `options.length > 0`
   * check: a service area can legitimately offer zero roads, and in that case pruning is exactly
   * what needs to happen.
   */
  const [loadedOptionsForKey, setLoadedOptionsForKey] = useState<string | null>(null);

  useEffect(() => {
    void fetchPcrFilterOptions()
      .then(setFilterOptions)
      .catch(() => setFilterOptions({ serviceAreas: [], foremanAreas: [], assets: [] }));
  }, []);

  const draftServiceAreaIdsKey = idsKey(draftServiceAreaIds);

  /**
   * Which service areas to load child options for.
   *
   * Normally the selected areas. But `GET /pcr/filter-options` returns EMPTY foreman/asset lists
   * when unscoped, so with no service area selected there would be no list to render a kept child
   * pick from — the pick would become an invisible, unremovable filter that Apply still submits. So
   * when the parent is empty and a child pick exists, fall back to all four WRAP areas: the picks
   * stay visible and removable, and the union implies no constraint.
   *
   * The fallback is deliberately conditional. The default dashboard URL selects no service area, and
   * each area's asset list is large, so fanning out on every plain page load would be a real cost.
   */
  const childOptionServiceAreaIds = useMemo(() => {
    if (draftServiceAreaIds.length > 0) return draftServiceAreaIds;
    const hasChildSelection = draftForemanAreaIds.length > 0 || draftRoadAssetIds.length > 0;
    return hasChildSelection ? [...PCR_SERVICE_AREA_IDS] : [];
  }, [draftServiceAreaIdsKey, draftForemanAreaIds.length, draftRoadAssetIds.length]);
  const childOptionServiceAreaIdsKey = idsKey(childOptionServiceAreaIds);

  /**
   * Foreman area / road options for the union of `childOptionServiceAreaIds`.
   *
   * GET /pcr/filter-options is scoped to one service area, so fan out (at most four — WRAP PCR only
   * has areas 16/17/22/25) and merge by id. Keeping the endpoint single-area avoids changing a
   * response shape that PCR promote-context also depends on.
   */
  useEffect(() => {
    if (childOptionServiceAreaIds.length === 0) {
      setRoadOptions([]);
      setForemanAreaOptions([]);
      // Nothing loaded, so pruning must stay off.
      setLoadedOptionsForKey(null);
      return;
    }
    let cancelled = false;
    // Invalidate immediately: between here and the responses landing, the lists on screen belong to
    // the PREVIOUS selection and must not be used to prune. The cleanup below plus this key are two
    // independent guards against an out-of-order response driving pruning.
    //
    // The key recorded is the SERVICE AREA SELECTION the lists describe, which is '' for the
    // all-areas fallback — and `canPrunePcrChildFilters` refuses to prune on an empty selection
    // anyway, so a kept unscoped pick can never be pruned away.
    const requestKey = draftServiceAreaIdsKey;
    setLoadedOptionsForKey(null);
    void Promise.all(
      childOptionServiceAreaIds.map((id) =>
        fetchPcrFilterOptions(id).catch(() => ({
          serviceAreas: [],
          foremanAreas: [] as PcrForemanAreaOption[],
          assets: [] as PcrFilterAssetOption[],
        })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const assetsById = new Map<number, PcrFilterAssetOption>();
      const foremanById = new Map<number, PcrForemanAreaOption>();
      for (const result of results) {
        for (const asset of result.assets) {
          if (!assetsById.has(asset.id)) assetsById.set(asset.id, asset);
        }
        for (const area of result.foremanAreas) {
          if (!foremanById.has(area.id)) foremanById.set(area.id, area);
        }
      }
      setRoadOptions(
        Array.from(assetsById.values()).sort((a, b) =>
          formatPcrRoadAssetLabel(a).localeCompare(formatPcrRoadAssetLabel(b)),
        ),
      );
      setForemanAreaOptions(
        Array.from(foremanById.values()).sort((a, b) => a.name.localeCompare(b.name)),
      );
      // Only now are the lists a truthful description of `requestKey`, so pruning may proceed.
      setLoadedOptionsForKey(requestKey);
    });
    return () => {
      cancelled = true;
    };
    // Keyed on a joined string so a new array with identical contents does not refire this.
  }, [childOptionServiceAreaIdsKey, draftServiceAreaIdsKey]);

  /**
   * Prune (never clear) the dependent filters once the option lists for the current service area
   * selection have loaded. A Foreman Area or Road pick that is still offered survives; only orphans
   * are dropped, which is how All Tasks and Submissions behave.
   *
   * `canPrunePcrChildFilters` refuses to act when no service area is selected, when the lists have
   * not loaded, or when they belong to an earlier selection. The setters return their previous array
   * by reference when nothing is pruned, so React bails out and this cannot loop.
   *
   * Pruning touches draft state only — the URL is written by Apply — so it never fights the
   * URL -> draft sync effect above.
   */
  useEffect(() => {
    if (
      !canPrunePcrChildFilters({
        serviceAreaIds: draftServiceAreaIds,
        loadedForKey: loadedOptionsForKey,
      })
    ) {
      return;
    }
    const availableForemanIds = foremanAreaOptions.map((area) => String(area.id));
    const availableRoadIds = roadOptions.map((asset) => String(asset.id));
    setDraftForemanAreaIds((prev) => prunePcrSelectedIds(prev, availableForemanIds));
    setDraftRoadAssetIds((prev) => prunePcrSelectedIds(prev, availableRoadIds));
  }, [loadedOptionsForKey, draftServiceAreaIdsKey, foremanAreaOptions, roadOptions]);

  useEffect(() => {
    setDraftFrom(listParams.from || '');
    setDraftTo(listParams.to || '');
    setDraftServiceAreaIds(listParams.serviceAreaId);
    setDraftForemanAreaIds(listParams.foremanAreaId);
    setDraftRoadAssetIds(listParams.roadAssetId);
    setDraftReportTypeBuckets(listParams.reportTypeBuckets ?? []);
    setDraftStatuses(listParams.statuses ?? []);
  }, [
    listParams.from,
    listParams.to,
    idsKey(listParams.serviceAreaId),
    idsKey(listParams.foremanAreaId),
    idsKey(listParams.roadAssetId),
    reportTypeBucketsKey(listParams.reportTypeBuckets),
    statusKey(listParams.statuses),
  ]);

  const metricsParams = useMemo(
    () => ({
      from: listParams.from,
      to: listParams.to,
      serviceArea: listParams.serviceArea,
      road: listParams.road,
      serviceAreaId: listParams.serviceAreaId,
      foremanAreaId: listParams.foremanAreaId,
      roadAssetId: listParams.roadAssetId,
      reportTypeBuckets: listParams.reportTypeBuckets,
      statuses: listParams.statuses,
      search: listParams.search,
    }),
    [
      listParams.from,
      listParams.to,
      listParams.serviceArea,
      listParams.road,
      idsKey(listParams.serviceAreaId),
      idsKey(listParams.foremanAreaId),
      idsKey(listParams.roadAssetId),
      reportTypeBucketsKey(listParams.reportTypeBuckets),
      statusKey(listParams.statuses),
      listParams.search,
    ],
  );

  const hasActiveListFilters = useMemo(
    () =>
      !!(
        listParams.from ||
        listParams.to ||
        listParams.serviceArea ||
        listParams.road ||
        listParams.serviceAreaId.length > 0 ||
        listParams.foremanAreaId.length > 0 ||
        listParams.roadAssetId.length > 0 ||
        listParams.search ||
        (listParams.reportTypeBuckets?.length ?? 0) > 0 ||
        !isDefaultPcrListStatusFilter(listParams.statuses)
      ),
    [
      listParams.from,
      listParams.to,
      listParams.serviceArea,
      listParams.road,
      idsKey(listParams.serviceAreaId),
      idsKey(listParams.foremanAreaId),
      idsKey(listParams.roadAssetId),
      listParams.search,
      reportTypeBucketsKey(listParams.reportTypeBuckets),
      statusKey(listParams.statuses),
    ],
  );

  /**
   * V2-076 / FLT-05: every applied filter that hides records, the default Status (New and
   * Internal Review, hiding Finalized and the rest) included. Status only stops counting when
   * every status is selected.
   */
  const appliedListFilterCount = [
    Boolean(listParams.from),
    Boolean(listParams.to),
    listParams.serviceAreaId.length > 0 || Boolean(listParams.serviceArea),
    listParams.foremanAreaId.length > 0,
    listParams.roadAssetId.length > 0 || Boolean(listParams.road),
    (listParams.reportTypeBuckets?.length ?? 0) > 0,
    PCR_STATUS_OPTIONS.some((o) => !listParams.statuses.includes(o.id as PcrStatus)),
  ].filter(Boolean).length;

  const hasUnappliedFilterChanges = useMemo(
    () =>
      draftFrom.trim() !== (listParams.from || '') ||
      draftTo.trim() !== (listParams.to || '') ||
      idsKey(draftServiceAreaIds) !== idsKey(listParams.serviceAreaId) ||
      idsKey(draftForemanAreaIds) !== idsKey(listParams.foremanAreaId) ||
      idsKey(draftRoadAssetIds) !== idsKey(listParams.roadAssetId) ||
      reportTypeBucketsKey(draftReportTypeBuckets) !== reportTypeBucketsKey(listParams.reportTypeBuckets) ||
      statusKey(draftStatuses) !== statusKey(listParams.statuses),
    [
      draftFrom,
      draftTo,
      draftServiceAreaIds,
      draftForemanAreaIds,
      draftRoadAssetIds,
      draftReportTypeBuckets,
      draftStatuses,
      listParams.from,
      listParams.to,
      idsKey(listParams.serviceAreaId),
      idsKey(listParams.foremanAreaId),
      idsKey(listParams.roadAssetId),
      reportTypeBucketsKey(listParams.reportTypeBuckets),
      statusKey(listParams.statuses),
    ],
  );

  const load = useCallback(async () => {
    if (!canViewSubmissions) {
      setSubmissions([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [listRes, met] = await Promise.all([
        fetchPcrSubmissionsPaged(listParams),
        fetchPcrMetrics(metricsParams),
      ]);
      setSubmissions(listRes.submissions);
      setTotal(listRes.total);
      setMetrics(met);
    } catch (e) {
      setSubmissions([]);
      setTotal(0);
      setMetrics(null);
      setError(e instanceof Error ? e.message : 'Could not load submissions.');
    } finally {
      setLoading(false);
    }
  }, [listParams, metricsParams, canViewSubmissions]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadGrouped = useCallback(async () => {
    if (!canViewSubmissions || !groupSimilar) return;
    setGroupedLoading(true);
    setGroupedError(null);
    try {
      const result = await fetchPcrGroupSuggestions(listParams);
      setGroupedResult(result);
    } catch (e) {
      setGroupedError(e instanceof Error ? e.message : 'Could not load grouped view.');
      setGroupedResult(null);
    } finally {
      setGroupedLoading(false);
    }
  }, [canViewSubmissions, groupSimilar, listParams]);

  useEffect(() => {
    if (groupSimilar) void loadGrouped();
    else setGroupedResult(null);
  }, [groupSimilar, loadGrouped]);

  const handleGenerateGroups = async () => {
    setGeneratingGroups(true);
    try {
      const result = await generatePcrGroupSuggestions();
      showToast(`Generated group suggestions for ${result.processed} PCRs.`);
      if (groupSimilar) void loadGrouped();
    } catch (e) {
      setGroupedError(e instanceof Error ? e.message : 'Failed to generate group suggestions.');
    } finally {
      setGeneratingGroups(false);
    }
  };

  const openLinkedWorkId = useCallback(
    (row: PcrSubmission) => {
      const q = (row.linkedWorkIdentificationId || row.workIdId || '').trim();
      if (!q) return;
      navigate(`/work-ids?search=${encodeURIComponent(q)}`);
    },
    [navigate],
  );

  const openLinkedWorkIdById = useCallback(
    (linkedWorkIdentificationId: string) => {
      if (!linkedWorkIdentificationId) return;
      navigate(`/work-ids?search=${encodeURIComponent(linkedWorkIdentificationId)}`);
    },
    [navigate],
  );

  const openDetailById = useCallback(
    (id: number) => {
      const submission = submissions.find((s) => s.id === String(id));
      if (submission) {
        setDetailModalRow(submission);
        setDetailStartInEditMode(false);
      }
    },
    [submissions],
  );

  const handleExportPcrAuditPdf = useCallback(
    async (row: PcrSubmission) => {
      const numericId = Number(row.id);
      if (!Number.isFinite(numericId)) return;
      try {
        setAuditReportExportingId(numericId);
        const blob = await downloadPcrAuditReportPdf(row.id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const label = (row.report_id || `PCR_${row.id}`).replace(/[/\\?*:<>|"\s]/g, '_');
        a.download = `PCR_Export_${label}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast('PCR export downloaded.');
      } catch (e) {
        console.error(e);
        showToast(e instanceof Error ? e.message : 'Failed to generate audit report', 'error');
      } finally {
        setAuditReportExportingId(null);
      }
    },
    [showToast],
  );

  const applyFilters = () => {
    const next = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, v: string) => {
      if (!v) next.delete(key);
      else next.set(key, v);
    };
    /** Multi-value filters write one repeated key per selection; empty removes the key entirely. */
    const setAllOrDelete = (key: string, values: string[]) => {
      next.delete(key);
      for (const value of values) {
        next.append(key, value);
      }
    };
    setOrDelete('from', draftFrom.trim());
    setOrDelete('to', draftTo.trim());
    setAllOrDelete('serviceAreaId', draftServiceAreaIds);
    setAllOrDelete('foremanAreaId', draftForemanAreaIds);
    setAllOrDelete('roadAssetId', draftRoadAssetIds);
    next.delete('serviceArea');
    next.delete('road');
    next.delete('assetId');
    next.delete('reportTypeBucket');
    next.delete('reportType');
    for (const b of draftReportTypeBuckets) {
      next.append('reportTypeBucket', b);
    }
    next.delete('status');
    const statusesToApply =
      draftStatuses.length > 0 ? draftStatuses : [...PCR_DEFAULT_LIST_STATUSES];
    for (const status of statusesToApply) {
      next.append('status', status);
    }
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    setDraftFrom('');
    setDraftTo('');
    setDraftServiceAreaIds([]);
    setDraftForemanAreaIds([]);
    setDraftRoadAssetIds([]);
    setDraftReportTypeBuckets([]);
    setDraftStatuses([...PCR_DEFAULT_LIST_STATUSES]);
    setSearchInput('');
    const next = new URLSearchParams();
    for (const status of PCR_DEFAULT_LIST_STATUSES) {
      next.append('status', status);
    }
    setSearchParams(next, { replace: true });
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next, { replace: true });
  };

  const setPageSize = (newSize: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('pageSize', String(newSize));
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const toggleSort = (column: 'createdAt' | 'status' | 'serviceArea' | 'roadName') => {
    const next = new URLSearchParams(searchParams);
    const cur = next.get('sortBy') || 'createdAt';
    const dir = next.get('sortDir') === 'asc' ? 'asc' : 'desc';
    if (cur === column) {
      next.set('sortDir', dir === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sortBy', column);
      next.set('sortDir', column === 'createdAt' ? 'desc' : 'asc');
    }
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const runMonthlyCsvExport = useCallback(
    async (month: string) => {
      setExporting(true);
      try {
        await downloadPcrMonthlyCsv(month, {
          from: listParams.from,
          to: listParams.to,
          serviceArea: listParams.serviceArea,
          road: listParams.road,
          serviceAreaId: listParams.serviceAreaId,
          foremanAreaId: listParams.foremanAreaId,
          roadAssetId: listParams.roadAssetId,
          reportTypeBuckets: listParams.reportTypeBuckets,
          statuses: listParams.statuses,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Export failed.');
      } finally {
        setExporting(false);
      }
    },
    [
      idsKey(listParams.foremanAreaId),
      listParams.from,
      listParams.reportTypeBuckets,
      listParams.road,
      idsKey(listParams.roadAssetId),
      listParams.serviceArea,
      idsKey(listParams.serviceAreaId),
      listParams.statuses,
      listParams.to,
    ],
  );

  const openExportCsvModal = useCallback(() => {
    setModalExportMonth(exportMonth);
    setExportCsvModalOpen(true);
  }, [exportMonth]);

  const confirmExportCsvFromModal = useCallback(async () => {
    if (!modalExportMonth.trim()) return;
    setExportMonth(modalExportMonth);
    setExportCsvModalOpen(false);
    await runMonthlyCsvExport(modalExportMonth);
  }, [modalExportMonth, runMonthlyCsvExport]);

  const handleStatusChange = async (row: PcrSubmission, status: PcrStatus) => {
    setStatusTransitioningId(row.id);
    try {
      const updated = await patchPcrSubmissionStatus(row.id, { status });
      await load();
      setSubmissions((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, status: updated.status } : item)),
      );
      if (detailModalRow?.id === row.id) {
        setDetailModalRow(updated);
      }
      const statusLabel = formatPcrStatusLabel(status);
      showToast(`PCR moved to ${statusLabel}.`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not update status.';
      setError(message);
      showToast(message, 'error');
    } finally {
      setStatusTransitioningId(null);
    }
  };

  const resetCreateForm = useCallback(() => {
    setCreateForm({
      report_id: '',
      service_area_text: '',
      community_text: '',
      road_text: '',
      report_type: '',
      activity_type: '',
      user_name: '',
      user_email: '',
      user_phone: '',
      contact_method: 'Email',
      message: '',
      image_url: '',
      is_feedback: false,
    });
    setContactChannel('email');
    setImageFile(null);
    setSelectedServiceAreaId(null);
    setSelectedContractAreaId(null);
    setSelectedForemanCommunityId(null);
    setContractAreas([]);
    setRoadAssets([]);
    setServiceAreas([]);
    setReportIdPreviews({});
  }, []);

  const loadPcrModalReferenceData = useCallback(async () => {
    setLoadingPcrFormRefs(true);
    try {
      const ctx = await fetchPcrCreateContext();
      setServiceAreas((ctx.serviceAreas ?? []).filter((sa) => isPcrServiceAreaName(sa.name)));
      setReportIdPreviews(ctx.nextReportIdPreviewByServiceAreaId ?? {});
    } catch {
      try {
        const fallbackServiceAreas = await areasService.getServiceAreas();
        setServiceAreas(
          (Array.isArray(fallbackServiceAreas) ? fallbackServiceAreas : [])
            .filter((sa) => isPcrServiceAreaName(sa.name))
            .map((sa) => ({
              id: sa.id,
              name: sa.name,
              code: sa.code ?? null,
            })),
        );
      } catch {
        setServiceAreas([]);
      }
      setReportIdPreviews({});
    } finally {
      setLoadingPcrFormRefs(false);
    }
  }, []);

  useEffect(() => {
    if (selectedServiceAreaId == null) {
      setCreateForm((prev) => (prev.report_id === '' ? prev : { ...prev, report_id: '' }));
      return;
    }
    const preview = reportIdPreviews[String(selectedServiceAreaId)];
    if (!preview) return;
    setCreateForm((prev) => (prev.report_id === preview ? prev : { ...prev, report_id: preview }));
  }, [selectedServiceAreaId, reportIdPreviews]);

  const openCreateModal = useCallback(() => {
    setCreateError(null);
    resetCreateForm();
    setShowCreateModal(true);
    void loadPcrModalReferenceData();
  }, [loadPcrModalReferenceData, resetCreateForm]);

  useEffect(() => {
    if (!showCreateModal || selectedServiceAreaId == null) {
      if (showCreateModal && selectedServiceAreaId == null) {
        setContractAreas([]);
      }
      return;
    }
    let cancelled = false;
    void fetchPcrContractAreasForServiceArea(selectedServiceAreaId)
      .then((rows) => {
        if (!cancelled) setContractAreas(rows);
      })
      .catch(() => {
        if (!cancelled) setContractAreas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showCreateModal, selectedServiceAreaId]);

  useEffect(() => {
    const noCommunity =
      selectedContractAreaId == null && selectedForemanCommunityId == null;
    if (!showCreateModal || selectedServiceAreaId == null || noCommunity) {
      if (showCreateModal && (selectedServiceAreaId == null || noCommunity)) {
        setRoadAssets([]);
      }
      return;
    }
    let cancelled = false;
    void assetService
      .searchAssetsLight({
        ...(selectedContractAreaId != null ? { contractAreaId: selectedContractAreaId } : {}),
        ...(selectedForemanCommunityId != null ? { foremanAreaId: selectedForemanCommunityId } : {}),
        serviceAreaId: selectedServiceAreaId,
        assetType: 'road',
      })
      .then((assets) => {
        if (!cancelled) {
          const normalized = Array.isArray(assets) ? assets : [];
          setRoadAssets(filterRoadAssets(dedupeAssetsById(normalized)));
        }
      })
      .catch(() => {
        if (!cancelled) setRoadAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showCreateModal, selectedServiceAreaId, selectedContractAreaId, selectedForemanCommunityId]);

  const handleCreateFieldChange = useCallback(
    (field: keyof CreatePcrSubmissionRequest, value: string | boolean) => {
      setCreateForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleCreatePcr = useCallback(async () => {
    if (!canCreatePcr) return;

    setIsCreating(true);
    setCreateError(null);
    try {
      const payload: CreatePcrSubmissionRequest = {
        ...createForm,
        user_email: contactChannel === 'email' ? (createForm.user_email ?? '') : '',
        user_phone: contactChannel === 'phone' ? (createForm.user_phone ?? '') : '',
        contact_method: contactChannel === 'email' ? 'Email' : 'Phone',
        image_url: imageFile ? '' : (createForm.image_url ?? ''),
        ...(selectedServiceAreaId != null ? { known_service_area_id: selectedServiceAreaId } : {}),
      };
      await createPcrSubmission(payload, imageFile);
      setShowCreateModal(false);
      resetCreateForm();
      showToast('PCR created successfully.');
      if (canViewSubmissions) {
        await load();
      }
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create PCR.');
    } finally {
      setIsCreating(false);
    }
  }, [
    canCreatePcr,
    canViewSubmissions,
    contactChannel,
    createForm,
    imageFile,
    load,
    resetCreateForm,
    selectedServiceAreaId,
    showToast,
  ]);

  const totalPages = Math.max(1, Math.ceil(total / (listParams.pageSize || 10)));
  const currentPage = listParams.page || 1;

  const roadDropdownOptions = useMemo(
    () =>
      roadOptions.map((a) => ({
        id: String(a.id),
        label: formatPcrRoadAssetLabel(a),
      })),
    [roadOptions],
  );

  const foremanDropdownOptions = useMemo(
    () =>
      foremanAreaOptions.map((area) => ({
        id: String(area.id),
        label: area.name,
      })),
    [foremanAreaOptions],
  );

  const roadLabelByToken = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of roadOptions) {
      const label = formatPcrRoadAssetLabel(option);
      for (const token of [option.assetNumber, option.name]) {
        const normalized = (token || '').trim().toLowerCase();
        if (normalized && !map.has(normalized)) {
          map.set(normalized, label);
        }
      }
    }
    return map;
  }, [roadOptions]);

  const formatRoadForRow = useCallback(
    (row: PcrSubmission) => {
      const normalized = (row.road_text || '').trim().toLowerCase();
      return roadLabelByToken.get(normalized) || row.road_text || '–';
    },
    [roadLabelByToken],
  );

  if (authLoading) {
    return <div className="tw-w-full tw-p-4">Loading…</div>;
  }

  return (
    // The shared list layout (W-H5, W-iP4): the page is a fixed-height column and the table box
    // under the filters takes all the height that is left, so it is the ONE vertical scroll area.
    <ListPage className="pcr-submissions-page">
      {/* The shared page header (W-M9): fixed order by intent with Create PCR always LAST (it
          used to sit mid-group, between Group Similar and Search). Labels at every width. The
          header's own Clear All is gone: the filter panel's Clear All also clears the search. */}
      <PageHeader
        title="PCRs"
        actions={[
          {
            key: 'refresh',
            label: loading ? 'Loading' : 'Refresh',
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            overflow: true,
            disabled: loading,
            title: 'Reload from the server',
            onClick: () => void load(),
          },
          {
            key: 'export',
            label: exporting ? 'Exporting...' : 'Download CSV',
            icon: 'bi-download',
            intent: 'neutral',
            overflow: true,
            disabled: exporting,
            title: 'Choose a month and download a CSV (uses the current filters)',
            onClick: openExportCsvModal,
          },
          {
            key: 'group-similar',
            label: 'Group Similar',
            icon: 'bi-layers',
            intent: 'secondary',
            active: groupSimilar,
            hidden: !canViewSubmissions,
            title: groupSimilar ? 'Show a flat list of submissions' : 'Group similar reports together',
            onClick: () => setGroupSimilar((v) => !v),
          },
          {
            key: 'regenerate',
            label: generatingGroups ? 'Regenerating...' : 'Regenerate',
            icon: 'bi-arrow-repeat',
            intent: 'secondary',
            hidden: !canViewSubmissions || !groupSimilar,
            disabled: generatingGroups,
            title: 'Re-score and group all recent PCRs',
            onClick: () => void handleGenerateGroups(),
          },
          {
            key: 'create',
            label: 'Create PCR',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreatePcr,
            title: 'Create a new PCR submission',
            onClick: openCreateModal,
          },
        ]}
      />

      <Modal
        show={exportCsvModalOpen}
        onHide={() => {
          if (!exporting) setExportCsvModalOpen(false);
        }}
        centered
        backdrop={exporting ? 'static' : true}
      >
        <Modal.Header closeButton={!exporting}>
          <Modal.Title>Download monthly CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="tw-mb-2" controlId="pcr-export-month-modal">
            <Form.Label>Export month</Form.Label>
            <Form.Control
              type="month"
              value={modalExportMonth}
              onChange={(e) => setModalExportMonth(e.target.value)}
              disabled={exporting}
            />
          </Form.Group>
          <p className="tw-text-content-muted tw-text-sm tw-mb-0">Uses the same filters as the table (date range, area, status, etc.).</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="outline-secondary"
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            disabled={exporting}
            onClick={() => setExportCsvModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            disabled={exporting || !modalExportMonth.trim()}
            onClick={() => void confirmExportCsvFromModal()}
          >
            {exporting ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-2" role="status" aria-hidden="true" />
                Exporting…
              </>
            ) : (
              <>
                <i className="bi bi-download tw-mr-2" />
                Download
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {canViewSubmissions && (
        <FiltersCard
            className="tw-w-full"
            title="Submission Filters"
            subtitle="Default view shows New and Internal Review. Filter by service area, foreman area, road asset, status, report type, and dates."
            collapsible
            // FiltersCard starts collapsed on a phone and on a tablet by itself (W-iP6).
            defaultCollapsed={false}
            appliedFilterCount={appliedListFilterCount}
            search={{
              value: searchInput,
              onChange: setSearchInput,
              onSearch: handleSearch,
              placeholder: 'Report ID, message, road, submitter or community',
              label: 'Search PCR submissions',
            }}
            showClearButton
            onClear={clearFilters}
            hasActiveFilters={hasActiveListFilters}
            showApplyButton
            onApply={applyFilters}
            applyDisabled={!hasUnappliedFilterChanges}
          >
            <FilterGrid>
              <FilterField>
                <StandoutDropdown
                  type="multiselect"
                  options={filterOptions.serviceAreas}
                  value={draftServiceAreaIds}
                  onChange={(vals) => {
                    // Dependent filters are intentionally NOT cleared here. They are pruned once the
                    // new option lists load (see the pruning effect), so still-valid Foreman Area and
                    // Road picks survive adding or removing a service area.
                    setDraftServiceAreaIds(
                      (vals as Array<string | number>)
                        .map((v) => String(v))
                        .filter((v) => (PCR_SERVICE_AREA_IDS as readonly string[]).includes(v)),
                    );
                  }}
                  getValue={(a) => String(a.id)}
                  formatOption={(a) => formatPcrServiceAreaLabel(a.id)}
                  placeholder="All Service Areas"
                  className="tw-w-full"
                  label="Service Area"
                  noOptionsMessage="No service areas"
                  maxDisplayItems={2}
                  showSelectedCount
                  preserveOptionsOrder
                />
              </FilterField>
              <FilterField>
                <StandoutDropdown
                  type="multiselect"
                  options={foremanDropdownOptions}
                  value={draftForemanAreaIds}
                  onChange={(vals) =>
                    setDraftForemanAreaIds((vals as Array<string | number>).map((v) => String(v)))
                  }
                  getValue={(o) => o.id}
                  formatOption={(o) => o.label}
                  placeholder={
                    childOptionServiceAreaIds.length > 0 ? 'All Foreman Areas' : 'Pick a service area'
                  }
                  className="tw-w-full"
                  label="Foreman Area"
                  noOptionsMessage={
                    childOptionServiceAreaIds.length > 0
                      ? 'No foreman areas found'
                      : 'Pick a service area'
                  }
                  // Usable whenever options are loaded, including the all-areas fallback that keeps
                  // a kept pick visible after the service area selection is emptied. Until then it
                  // is DISABLED, not read-only: read-only hid the chevron, so the field looked like
                  // a plain text box (V2-025).
                  disabled={childOptionServiceAreaIds.length === 0}
                  style={childOptionServiceAreaIds.length === 0 ? DISABLED_DROPDOWN_STYLE : undefined}
                  maxDisplayItems={2}
                  showSelectedCount
                  preserveOptionsOrder
                />
              </FilterField>
              <FilterField>
                <StandoutDropdown
                  type="multiselect"
                  options={roadDropdownOptions}
                  value={draftRoadAssetIds}
                  onChange={(vals) =>
                    setDraftRoadAssetIds((vals as Array<string | number>).map((v) => String(v)))
                  }
                  getValue={(o) => o.id}
                  formatOption={(o) => o.label}
                  placeholder={
                    childOptionServiceAreaIds.length > 0
                      ? 'All Roads (Assets)'
                      // Short enough to fit the field (V2-025: "Select service area firs..." was cut off).
                      : 'Pick a service area'
                  }
                  className="tw-w-full"
                  label="Road (Asset)"
                  noOptionsMessage={
                    childOptionServiceAreaIds.length > 0
                      ? 'No assets for this area'
                      : 'Pick a service area'
                  }
                  disabled={childOptionServiceAreaIds.length === 0}
                  style={childOptionServiceAreaIds.length === 0 ? DISABLED_DROPDOWN_STYLE : undefined}
                  maxDisplayItems={2}
                  showSelectedCount
                  preserveOptionsOrder
                />
              </FilterField>
              <FilterField>
                <StandoutDropdown
                  type="multiselect"
                  options={[...PCR_STATUS_OPTIONS]}
                  value={draftStatuses}
                  onChange={(vals) => {
                    const normalized = (vals as string[])
                      .map((v) => normalizePcrStatusParam(v))
                      .filter((v): v is PcrStatus => v != null);
                    setDraftStatuses(
                      normalized.length > 0 ? normalized : [...PCR_DEFAULT_LIST_STATUSES],
                    );
                  }}
                  getValue={(o) => o.id}
                  formatOption={(o) => o.name}
                  placeholder="All Statuses"
                  className="tw-w-full"
                  label="Status"
                  maxDisplayItems={3}
                  showSelectedCount
                  // PCR lifecycle order (New -> Internal Review -> Finalized), not alphabetical.
                  preserveOptionsOrder
                />
              </FilterField>
              <FilterField>
                <StandoutDropdown
                  type="multiselect"
                  options={PCR_REPORT_TYPE_MULTI_OPTIONS}
                  value={draftReportTypeBuckets}
                  onChange={(vals) =>
                    setDraftReportTypeBuckets(
                      (vals as string[]).filter((v): v is PcrReportTypeBucket =>
                        ['Requests', 'Compliments', 'Complaints', 'N/A'].includes(v),
                      ),
                    )
                  }
                  getValue={(o) => o.id}
                  formatOption={(o) => o.name}
                  placeholder="All Report Types"
                  className="tw-w-full"
                  label="Report Type"
                  maxDisplayItems={3}
                  showSelectedCount
                  // Curated order with N/A last, not alphabetical.
                  preserveOptionsOrder
                />
              </FilterField>
              <FilterField>
                <Form.Label>Created From</Form.Label>
                <Form.Control
                  type="date"
                  value={draftFrom}
                  onChange={(e) => setDraftFrom(e.target.value)}
                />
              </FilterField>
              <FilterField>
                <Form.Label>Created To</Form.Label>
                <Form.Control
                  type="date"
                  value={draftTo}
                  onChange={(e) => setDraftTo(e.target.value)}
                />
              </FilterField>
            </FilterGrid>
          </FiltersCard>
      )}
      {/* Summary Totals: a compact one-line strip of counts (W-iP4). It used to be a tall
          click-to-expand card that, expanded, ate most of an iPad's height above the table. */}
      {canViewSubmissions && <PcrMetricsSummaryCard metrics={metrics} loading={loading} />}

      {/* Plain-language notes on what this role cannot do (no internal permission codes). */}
      {canViewSubmissions && !canPatchPcrSubmission && !canSetPcrInternalReview && !canFinalizePcr && (
        <Alert variant="info" className="tw-mb-3 tw-py-2 tw-text-sm" data-testid="pcr-view-only-note">
          You can view PCR submissions but not edit them, move them to Internal Review or finalize them. Ask your
          administrator if you need to.
        </Alert>
      )}

      {canViewSubmissions && canEditPcrWorkflow && !canFinalizePcr && (
        <Alert variant="warning" className="tw-mb-3 tw-py-2 tw-text-sm" data-testid="pcr-no-finalize-note">
          Finalizing PCRs (bulk close, close without a Work ID, promote or link to a Work ID, or setting the status to
          Finalized) is limited to Ops Managers and Admins.
        </Alert>
      )}

      <ListPage.Body>
        {canViewSubmissions ? (
          groupSimilar ? (
            <PcrGroupedTable
              result={groupedResult}
              loading={groupedLoading}
              error={groupedError}
              onDismissError={() => setGroupedError(null)}
              onOpenDetail={openDetailById}
              onOpenLinkedWorkId={openLinkedWorkIdById}
              onGroupChange={() => void loadGrouped()}
              onBulkSuccess={(msg, toastType) => showToast(msg, toastType ?? 'success')}
              canClose={canFinalizePcr}
              canAddNote={canOpenNotesHistory}
              canLinkWorkId={canPromoteToWorkId}
            />
          ) : (
            <PcrSubmissionsTable
              submissions={submissions}
              loading={loading}
              error={error}
              onDismissError={() => setError(null)}
              canUpdateStatus={canEditPcrWorkflow}
              canMoveToInternalReview={canSetPcrInternalReview}
              onMoveToInternalReview={(row) => void handleStatusChange(row, 'internal_review')}
              canMoveToFinalize={canFinalizePcr}
              onMoveToFinalize={(row) => void handleStatusChange(row, 'finalized')}
              statusTransitioningId={statusTransitioningId}
              pcrReplyUiEnabled={pcrReplyUiEnabled}
              canPcrReply={canPcrReply}
              onReply={(row) => setReplyModalRow(row)}
              onEdit={
                canEditPcrWorkflow
                  ? (row) => {
                      setDetailModalRow(row);
                      setDetailStartInEditMode(true);
                    }
                  : undefined
              }
              onOpenDetail={(row) => {
                setDetailModalRow(row);
                setDetailStartInEditMode(false);
              }}
              formatRoadLabel={formatRoadForRow}
              sortBy={listParams.sortBy}
              sortDir={listParams.sortDir}
              onSort={toggleSort}
              canOpenNotesHistory={canOpenNotesHistory}
              onOpenNotesHistory={(row) => setNotesHistorySubmission(row)}
              canPromoteToWorkId={canPromoteToWorkId}
              onPromoteToWorkId={canPromoteToWorkId ? (row) => setPromoteModalRow(row) : undefined}
              canCloseWithoutWork={canFinalizePcr}
              onCloseWithoutWork={canFinalizePcr ? (row) => setCloseWithoutWorkRow(row) : undefined}
              onOpenLinkedWorkId={canViewSubmissions ? openLinkedWorkId : undefined}
              canExportAuditReport={canOpenNotesHistory}
              onExportAuditReport={canOpenNotesHistory ? handleExportPcrAuditPdf : undefined}
              auditReportExportingId={auditReportExportingId}
            />
          )
        ) : (
          <div
            className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-4 tw-text-sm tw-text-content-muted"
            data-testid="pcr-no-access"
          >
            You don&apos;t have access to view PCR submissions. Ask your administrator if you need it.
            {canCreatePcr && ' You can still create a new PCR with Create PCR.'}
          </div>
        )}
      </ListPage.Body>
      {canViewSubmissions && !groupSimilar ? (
        <ServerPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={total}
          pageSize={listParams.pageSize || 20}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
          className="tw-mt-2"
        />
      ) : null}

      <PcrReplyModal
        isOpen={replyModalRow != null}
        submission={replyModalRow}
        onClose={() => setReplyModalRow(null)}
        onViewContactLog={() => {
          if (!replyModalRow) return;
          setNotesHistoryInitialTab('contact');
          setNotesHistorySubmission(replyModalRow);
          setReplyModalRow(null);
        }}
        onSent={() => {
          const sentId = replyModalRow?.id;
          showToast('Reply sent to the submitter.');
          void load();
          if (sentId != null && notesHistorySubmission?.id === sentId) {
            setContactLogRefreshToken((n) => n + 1);
          }
        }}
      />

      <PcrPromoteToWorkIdModal
        visible={promoteModalRow != null}
        row={promoteModalRow}
        onClose={() => setPromoteModalRow(null)}
        onSuccess={({ submission, workIdentificationId, workIdId }) => {
          showToast(
            `Work ID ${workIdentificationId ?? workIdId} created from PCR ${submission.report_id || submission.id}. The PCR is finalized; WRAP stored the Work ID creation timestamp for internal reporting (not a contractual response-time clock).`,
          );
          void load();
        }}
      />

      <PcrCloseWithoutWorkModal
        visible={closeWithoutWorkRow != null}
        row={closeWithoutWorkRow}
        onClose={() => setCloseWithoutWorkRow(null)}
        onSuccess={() => {
          showToast('PCR finalized with no Work ID.');
          void load();
        }}
      />

      <PcrSubmissionDetailModal
        isOpen={detailModalRow != null}
        submission={detailModalRow}
        canPatchPcrSubmission={canPatchPcrSubmission}
        canSetInternalReview={canSetPcrInternalReview}
        canFinalizePcr={canFinalizePcr}
        startInEditMode={detailStartInEditMode}
        onSubmissionUpdated={load}
        onClose={() => {
          setDetailModalRow(null);
          setDetailStartInEditMode(false);
        }}
        onSaveSubmission={
          detailModalRow
            ? async (input) => {
                const updated = await patchPcrSubmission(detailModalRow.id, input);
                setDetailModalRow(updated);
              }
            : undefined
        }
        onOpenLinkedWorkId={canViewSubmissions ? openLinkedWorkId : undefined}
      />

      {showCreateModal && (
        <ResponsiveModal
          isOpen
          onClose={() => {
            if (isCreating) return;
            setShowCreateModal(false);
            setCreateError(null);
          }}
          title="Create PCR"
          size="large"
          footer={
            <>
              <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={isCreating}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleCreatePcr()} disabled={isCreating}>
                {isCreating ? 'Creating…' : 'Create PCR'}
              </Button>
            </>
          }
        >
          {createError && (
            <Alert variant="danger" className="tw-mb-3">
              {createError}
            </Alert>
          )}
          <Form className="pcr-create-form">
            {loadingPcrFormRefs && (
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-content-muted tw-text-sm tw-mb-3">
                <Spinner animation="border" size="sm" role="status" />
                Loading form options…
              </div>
            )}

            {/* Location */}
            <div className="tw-mb-4">
              <strong className="tw-block tw-mb-3">Location</strong>
              <Row>
                <Col xs={12} md={4}>
                  <Form.Group controlId="pcr-service-area">
                    <Form.Label>
                      Service area <span className="tw-text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={selectedServiceAreaId != null ? String(selectedServiceAreaId) : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          setSelectedServiceAreaId(null);
                          setSelectedContractAreaId(null);
                          setSelectedForemanCommunityId(null);
                          handleCreateFieldChange('service_area_text', '');
                          handleCreateFieldChange('community_text', '');
                          handleCreateFieldChange('road_text', '');
                          return;
                        }
                        const id = parseInt(v, 10);
                        const area = serviceAreas.find((a) => a.id === id);
                        setSelectedServiceAreaId(id);
                        setSelectedContractAreaId(null);
                        setSelectedForemanCommunityId(null);
                        handleCreateFieldChange('service_area_text', area?.name ?? '');
                        handleCreateFieldChange('community_text', '');
                        handleCreateFieldChange('road_text', '');
                      }}
                    >
                      <option value="">Select service area…</option>
                      {serviceAreas.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group controlId="pcr-community">
                    <Form.Label>Community</Form.Label>
                    <Form.Select
                      disabled={selectedServiceAreaId == null}
                      value={
                        selectedForemanCommunityId != null
                          ? `foreman:${selectedForemanCommunityId}`
                          : selectedContractAreaId != null
                            ? `contract:${selectedContractAreaId}`
                            : ''
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          setSelectedContractAreaId(null);
                          setSelectedForemanCommunityId(null);
                          handleCreateFieldChange('community_text', '');
                          handleCreateFieldChange('road_text', '');
                          return;
                        }
                        const colon = val.indexOf(':');
                        if (colon < 1) return;
                        const kind = val.slice(0, colon);
                        const id = parseInt(val.slice(colon + 1), 10);
                        if (Number.isNaN(id)) return;
                        const row = contractAreas.find((c) => (c.kind ?? 'contract') === kind && c.id === id);
                        if (kind === 'foreman') {
                          setSelectedContractAreaId(null);
                          setSelectedForemanCommunityId(id);
                        } else {
                          setSelectedForemanCommunityId(null);
                          setSelectedContractAreaId(id);
                        }
                        handleCreateFieldChange('community_text', row?.name ?? '');
                        handleCreateFieldChange('road_text', '');
                      }}
                    >
                      <option value="">Select community…</option>
                      {contractAreas.map((c) => {
                        const k = c.kind ?? 'contract';
                        return (
                          <option key={`${k}-${c.id}`} value={`${k}:${c.id}`}>
                            {c.name}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group controlId="pcr-report-id">
                    <Form.Label>Report ID</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>#</InputGroup.Text>
                      <Form.Control
                        readOnly
                        value={createForm.report_id ?? ''}
                        className="tw-bg-surface-alt"
                        title="Auto-assigned based on service area"
                      />
                    </InputGroup>
                    <Form.Text className="tw-text-content-muted">
                      Preview – the server assigns the final ID on save.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="pcr-road">
                    <Form.Label>Road (asset)</Form.Label>
                    <Form.Select
                      disabled={
                        selectedServiceAreaId == null ||
                        (selectedContractAreaId == null && selectedForemanCommunityId == null)
                      }
                      value={createForm.road_text ?? ''}
                      onChange={(e) => handleCreateFieldChange('road_text', e.target.value)}
                    >
                      <option value="">
                        {selectedServiceAreaId == null
                          ? 'Select a service area first'
                          : selectedContractAreaId == null && selectedForemanCommunityId == null
                            ? 'Select a community first'
                            : 'Select road…'}
                      </option>
                      {roadAssets.map((r) => {
                        const label = `${r.name} (${r.assetNumber})`;
                        return (
                          <option key={r.id} value={label}>
                            {label}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="pcr-report-type">
                    <Form.Label>Report type</Form.Label>
                    <Form.Select
                      value={createForm.report_type ?? ''}
                      onChange={(e) => handleCreateFieldChange('report_type', e.target.value)}
                    >
                      <option value="">Select…</option>
                      {PCR_REPORT_TYPE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="pcr-activity-type">
                    <Form.Label>Activity type</Form.Label>
                    <Form.Select
                      value={createForm.activity_type ?? ''}
                      onChange={(e) => handleCreateFieldChange('activity_type', e.target.value)}
                    >
                      <option value="">Select…</option>
                      {PCR_ACTIVITY_TYPE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Message */}
            <div className="tw-mb-4">
              <strong className="tw-block tw-mb-3">Message</strong>
              <Form.Group controlId="pcr-message">
                <Form.Label>
                  Details <span className="tw-text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Describe the issue or request"
                  value={createForm.message ?? ''}
                  onChange={(e) => handleCreateFieldChange('message', e.target.value)}
                />
              </Form.Group>
            </div>

            {/* Contact */}
            <div className="tw-mb-3">
              <strong className="tw-block tw-mb-3">Contact</strong>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="pcr-user-name">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      autoComplete="name"
                      placeholder="Full name"
                      value={createForm.user_name ?? ''}
                      onChange={(e) => handleCreateFieldChange('user_name', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="pcr-contact-channel">
                    <Form.Label>Preferred contact</Form.Label>
                    <Form.Select
                      value={contactChannel}
                      onChange={(e) => setContactChannel(e.target.value as 'email' | 'phone')}
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {contactChannel === 'email' && (
                  <Col xs={12} md={6}>
                    <Form.Group controlId="pcr-user-email">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        autoComplete="email"
                        value={createForm.user_email ?? ''}
                        onChange={(e) => handleCreateFieldChange('user_email', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                )}
                {contactChannel === 'phone' && (
                  <Col xs={12} md={6}>
                    <Form.Group controlId="pcr-user-phone">
                      <Form.Label>Phone number</Form.Label>
                      <Form.Control
                        type="tel"
                        autoComplete="tel"
                        value={createForm.user_phone ?? ''}
                        onChange={(e) => handleCreateFieldChange('user_phone', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                )}
                <Col xs={12} md={6}>
                  <Form.Group controlId="pcr-is-feedback">
                    <Form.Label>Requires feedback?</Form.Label>
                    <Form.Select
                      value={createForm.is_feedback ? 'yes' : 'no'}
                      onChange={(e) => handleCreateFieldChange('is_feedback', e.target.value === 'yes')}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="pcr-image-file">
                    <Form.Label>Photo (optional)</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      disabled={isCreating}
                      onChange={(e) => {
                        const input = e.target as HTMLInputElement;
                        setImageFile(input.files?.[0] ?? null);
                      }}
                    />
                    {imageFile && (
                      <Form.Text className="tw-text-content-muted tw-block">Selected: {imageFile.name}</Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Form>
        </ResponsiveModal>
      )}

      <PcrNotesHistoryModal
        visible={!!notesHistorySubmission}
        submission={notesHistorySubmission}
        initialTab={notesHistoryInitialTab}
        contactLogRefreshToken={contactLogRefreshToken}
        onClose={() => {
          setNotesHistorySubmission(null);
          setNotesHistoryInitialTab('notes');
        }}
        onCommentAdded={() => void load()}
      />
    </ListPage>
  );
};

export default PcrSubmissionsDashboardPage;
